/**
 * @render react
 * @name Review
 */

import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import uniqid from 'uniqid';

import { withPolaris } from 'Components/protons/Polaris';

import { Image } from 'Components/atoms/Image';
import { Heading } from 'Components/molecules/Heading';
import { Link } from 'Components/molecules/Link';
import { Rating } from 'Components/molecules/Rating';

import './_style.scss';

/**
 * Review component
 *
 * @param {integer} reviewIndex - index to be shown on the FE
 * @param {object} image - Image to be used
 * @param {string} imageSize - Image size (transformation to be used)
 * @param {string} title - title for the component
 * @param {string} url - URL for the title
 * @param {string} description - description to be displayed below the title
 * @param {array} ratings - array of objects with the following keys:
 *                @param {string} label
 *                @param {string} value
 * @param {string} readMoreLabel - label for read more
 * @param {string} allReviewsLabel - label for allReviews
 * @param {string} allReviewsLabelExtraClassNames - extra classes to be added to the label for allReviews
 * @param {string} allReviewsUrl - URL for all reviews
 * @param {string} allReviewsWrapperExtraClassNames - extra classes to be added to the wrapper of URL for all reviews
 * @param {bool} showCustomRating - whether to show custom rating or use the Rating component to display stars
 * @param {node} children - component children
 * @param {boolean} loadStyles - whether to apply the class names for styling
 * @param {Object} extraClassNames - extra classes to be added to the component
 * @param {Object} context - extra information coming from Polaris provider
 * @param {Object} watchVideo - parameters of the Watch Video element
 */
const Review = (
  {
    reviewIndex,
    image,
    imageSize,
    title,
    url,
    description,
    ratings,
    readMoreLabel,
    allReviewsLabel,
    allReviewsLabelExtraClassNames,
    allReviewsUrl,
    allReviewsWrapperExtraClassNames,
    showCustomRating,
    children,
    loadStyles,
    extraClassNames,
    context: { cssPrefix },
    watchVideo,
  },
) => (
  <div className={classNames({
    [`${cssPrefix}__review`]: loadStyles,
    ...extraClassNames,
  })}
  >
    <Heading
      href={url}
      extraClassNames={{ [`${cssPrefix}__review--title`]: true }}
    >
      {reviewIndex && (
        <span
          className={`${cssPrefix}__review--index`}
          data-index={reviewIndex}
        />
      )}
      {title}
    </Heading>

    {image && image.src && (
      <div className={`${cssPrefix}__review--media`}>
        <Image {...image} size={imageSize} lazyLoad />
      </div>
    )}

    <div className={`${cssPrefix}__review--description`}>
      {description}

      {readMoreLabel && (
        <span className={`${cssPrefix}__review--read-more`}>
          <Link href={url}>{readMoreLabel}</Link>
        </span>
      )}
    </div>

    {ratings.length > 0 && (
      <div className={`${cssPrefix}__review--ratings`}>
        {showCustomRating ? (
          <>
            {ratings.map((rating, index) => (
              <div
                className={`${cssPrefix}__review--rating -custom`}
                key={uniqid(`review-single-${index}`)}
              >
                <span className={`${cssPrefix}__review--rating--label`}>
                  {`${rating.label}: `}
                </span>
                <span className={`${cssPrefix}__review--rating--value`}>
                  {rating.value}
                </span>
              </div>
            ))}
          </>
        ) : (
          <>
            {ratings.map((rating, index) => (
              <div
                className={`${cssPrefix}__review--single-rating`}
                key={uniqid(`review-single-rating-${index}`)}
              >
                <div className={`${cssPrefix}__review--rating`}>
                  <Rating
                    label={rating.label}
                    rating={parseFloat(rating.value)}
                    rounding={rating.rounding || 0}
                    showTextRating
                  />
                </div>

                {allReviewsLabel && allReviewsUrl && (
                  <span
                    className={classNames({
                      [`${cssPrefix}__review--all-reviews`]: true,
                      ...allReviewsWrapperExtraClassNames,
                    })}
                  >
                    <Link href={allReviewsUrl}>
                      <span className={classNames({
                        ...allReviewsLabelExtraClassNames,
                      })}
                      >
                        {allReviewsLabel}
                      </span>
                    </Link>
                  </span>
                )}

                {watchVideo && watchVideo.label && watchVideo.url && (
                  <span className={classNames({
                    ...watchVideo.wrapperExtraClassNames,
                  })}
                  >
                    <Link href={watchVideo.url}>
                      <span className={classNames({
                        ...watchVideo.labelExtraClassNames,
                      })}
                      >
                        {watchVideo.label}
                      </span>
                    </Link>
                  </span>
                )}
              </div>
            ))}
          </>
        )}
      </div>
    )}

    {React.Children.count(children) > 0 && (
      <div className={`${cssPrefix}__review--children`}>
        {children}
      </div>
    )}
  </div>
);

/**
 * Valid props
 */
Review.propTypes = {
  reviewIndex: PropTypes.number,
  title: PropTypes.string,
  image: PropTypes.shape(),
  imageSize: PropTypes.string,
  url: PropTypes.string,
  description: PropTypes.string,
  ratings: PropTypes.arrayOf(PropTypes.shape()),
  readMoreLabel: PropTypes.string,
  allReviewsLabel: PropTypes.string,
  allReviewsLabelExtraClassNames: PropTypes.shape(),
  allReviewsUrl: PropTypes.string,
  allReviewsWrapperExtraClassNames: PropTypes.shape(),
  showCustomRating: PropTypes.bool,
  children: PropTypes.node,
  loadStyles: PropTypes.bool,
  context: PropTypes.shape({
    cssPrefix: PropTypes.string,
  }),
  extraClassNames: PropTypes.shape(),
  watchVideo: PropTypes.shape({
    label: PropTypes.string,
    url: PropTypes.string,
    labelExtraClassNames: PropTypes.shape(),
    wrapperExtraClassNames: PropTypes.shape(),
  }),
};

/**
 * Default props
 */
Review.defaultProps = {
  reviewIndex: null,
  title: '',
  image: null,
  imageSize: 'content-image-full',
  url: '',
  description: '',
  readMoreLabel: '',
  allReviewsLabel: '',
  allReviewsLabelExtraClassNames: {},
  allReviewsUrl: '',
  allReviewsWrapperExtraClassNames: {},
  showCustomRating: true,
  ratings: [],
  children: [],
  loadStyles: true,
  context: {},
  extraClassNames: {},
  watchVideo: {},
};

export default withPolaris(Review);
